import { z } from 'zod'

/**
 * How a species is DRAWN on a map: a shape and a color.
 *
 * Species themselves are configured in the app — see `TreeSpeciesDto` — but
 * their marks are not free-form, and that is deliberate on three counts.
 *
 * **Tailwind cannot generate a class it never sees.** It scans source text, so
 * `fill-map-${color}` assembled at runtime produces a pin with no color at
 * all. Every class has to be written out somewhere, which means the set of
 * colors has to be closed.
 *
 * **Shape carries the species a second time.** Hue alone fails a color-blind
 * reader and fails again in grayscale print, and these maps are printed. Two
 * species sharing a color are still told apart by their outline.
 *
 * **A palette keeps a map looking like one map.** A free color picker produces
 * a legend of twelve hues nobody chose together, on top of aerial photography
 * that is already busy. These values are sampled from the printed deliverables
 * and extended in the same register.
 */

export const TREE_MARK_SHAPES = [
  'SQUARE',
  'TRIANGLE',
  'CIRCLE',
  'STAR',
  'HEART',
  'CROSS',
  'DIAMOND',
  'HEXAGON',
  'PENTAGON',
] as const

export const treeMarkShapeSchema = z.enum(TREE_MARK_SHAPES)

export type TreeMarkShape = z.infer<typeof treeMarkShapeSchema>

export const TREE_MARK_SHAPE_LABELS: Record<TreeMarkShape, string> = {
  SQUARE: 'Square',
  TRIANGLE: 'Triangle',
  CIRCLE: 'Circle',
  STAR: 'Star',
  HEART: 'Heart',
  CROSS: 'Cross',
  DIAMOND: 'Diamond',
  HEXAGON: 'Hexagon',
  PENTAGON: 'Pentagon',
}

export const TREE_MARK_COLORS = [
  'ORANGE',
  'VIOLET',
  'GREEN',
  'SLATE',
  'ROSE',
  'MAGENTA',
  'GREY',
  'TEAL',
  'BLUE',
  'CRIMSON',
  'OLIVE',
  'BROWN',
] as const

export const treeMarkColorSchema = z.enum(TREE_MARK_COLORS)

export type TreeMarkColor = z.infer<typeof treeMarkColorSchema>

export const TREE_MARK_COLOR_LABELS: Record<TreeMarkColor, string> = {
  ORANGE: 'Orange',
  VIOLET: 'Violet',
  GREEN: 'Green',
  SLATE: 'Slate',
  ROSE: 'Rose',
  MAGENTA: 'Magenta',
  GREY: 'Gray',
  TEAL: 'Teal',
  BLUE: 'Blue',
  CRIMSON: 'Crimson',
  OLIVE: 'Olive',
  BROWN: 'Brown',
}

/**
 * The mark shown for a tree nobody has identified.
 *
 * A gray diamond, matching what "Not identified" has always looked like. It is
 * a constant rather than a row because an unidentified tree has no species
 * record to read a mark from — `Tree.speciesId` is simply null.
 */
export const UNIDENTIFIED_MARK = {
  shape: 'DIAMOND',
  color: 'GREY',
} as const satisfies { shape: TreeMarkShape; color: TreeMarkColor }

/** What an unidentified tree is called wherever a species name would go. */
export const UNIDENTIFIED_LABEL = 'Not identified'
